import {
  Badge,
  Button,
  Card,
  KeyValueGrid,
  MetricCard,
} from '@/components';
import { Compass, Crosshair } from 'lucide-react';
import { useActiveFocus, useData, useUI } from '@/data/stores';
import './settings.css';

const ROLES = [
  { name: 'Platform Admin', count: 2, perms: 'Full system' },
  { name: 'Security Analyst', count: 8, perms: 'Full visibility, alert + policy editing' },
  { name: 'Operations Analyst', count: 14, perms: 'Asset, twin, scenarios, reports' },
  { name: 'Executive', count: 5, perms: 'Read-only dashboards, reports' },
  { name: 'Compliance Officer', count: 3, perms: 'Compliance dashboards, evidence export' },
  { name: 'Incident Responder', count: 4, perms: 'Triage, investigation, containment' },
  { name: 'External Auditor', count: 1, perms: 'Read-only, time-limited' },
  { name: 'API Service Account', count: 6, perms: 'Programmatic, scoped' },
];

const DEPLOYMENTS = [
  { name: 'Sovereign On-Premise', sites: 3, status: 'active' },
  { name: 'Private Cloud (AWS GovCloud EU)', sites: 1, status: 'active' },
  { name: 'Hybrid', sites: 6, status: 'active' },
  { name: 'Air-Gapped', sites: 1, status: 'active' },
];

export function SettingsView() {
  const { sensors, otDevices, focusModes } = useData();
  const activeFocus = useActiveFocus();
  const setFocusMode = useUI((s) => s.setFocusMode);
  const startTour = useUI((s) => s.startTour);
  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Settings & System</h1>
          <p className="caption">
            Users, roles, deployments, sensor fleet, and platform configuration.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Compass size={15} strokeWidth={1.6} />}
          onClick={startTour}
        >
          Tour
        </Button>
      </header>

      <section className="iris-page-metrics">
        <MetricCard label="Active users" value={ROLES.reduce((s, r) => s + r.count, 0)} />
        <MetricCard label="Roles" value={ROLES.length} />
        <MetricCard label="Deployments" value={DEPLOYMENTS.length} />
        <MetricCard label="Sensors" value={sensors.length} />
        <MetricCard label="OT devices" value={otDevices.length} />
        <MetricCard label="Platform" value="v0.1.0" caption="rolling" />
      </section>

      <Card
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Crosshair size={12} strokeWidth={1.5} /> Focus modes
          </span>
        }
        subtitle="A focus mode reshapes the COP, the map, applicable compliance frameworks, alerts, and threat-actor emphasis. It does not delete or alter underlying data - only what the operator sees and where the map flies."
      >
        <div className="iris-focus-list">
          {focusModes.map((mode) => {
            const active = mode.id === activeFocus.id;
            return (
              <button
                key={mode.id}
                className={`iris-focus-row ${active ? 'on' : ''}`}
                onClick={() => setFocusMode(mode.id)}
              >
                <div className="iris-focus-row-head">
                  <span className="iris-focus-shortlabel mono">{mode.short_label}</span>
                  <span className="iris-focus-label">{mode.label}</span>
                  {active && (
                    <Badge color="var(--status-info)" style={{ marginLeft: 'auto' }}>
                      Active
                    </Badge>
                  )}
                </div>
                <div className="iris-focus-desc">{mode.description}</div>
                <div className="iris-focus-meta">
                  <span>
                    Frameworks:{' '}
                    <span className="mono">{mode.applicable_frameworks.join(' · ') || '-'}</span>
                  </span>
                  <span>
                    Threat-actor emphasis:{' '}
                    <span className="mono">
                      {mode.emphasis.threat_actors.join(', ') || '-'}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="iris-settings-grid">
        <Card title="Users & roles" subtitle="RBAC summary across the platform">
          <table className="iris-table">
            <thead>
              <tr>
                <th>Role</th>
                <th style={{ textAlign: 'right' }}>Users</th>
                <th>Permissions</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    {r.count}
                  </td>
                  <td className="caption">{r.perms}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Deployment models" subtitle="Active per spec section 8.1">
          <ul className="iris-deployments">
            {DEPLOYMENTS.map((d) => (
              <li key={d.name}>
                <span>{d.name}</span>
                <span className="mono">{d.sites} sites</span>
                <Badge color="var(--status-operational)">{d.status}</Badge>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Encryption & auth" subtitle="Spec §8.4">
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Transport', value: 'TLS 1.3', mono: true },
              { label: 'At rest', value: 'AES-256-GCM', mono: true },
              { label: 'Auth', value: 'SAML 2.0 + OAuth2 + LDAP/AD' },
              { label: 'MFA', value: 'Mandatory (TOTP, WebAuthn)' },
              { label: 'Audit log', value: 'Immutable, signed', mono: true },
              { label: 'Key management', value: 'HSM-backed' },
            ]}
          />
        </Card>

        <Card title="Data retention" subtitle="PCAP, alerts, sensor telemetry">
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'PCAP', value: '180 days (compressed zstd)', mono: true },
              { label: 'Alerts', value: '5 years', mono: true },
              { label: 'Sensor telemetry', value: '2 years', mono: true },
              { label: 'Forensic snapshots', value: '7 years', mono: true },
            ]}
          />
        </Card>

        <Card title="External feeds" subtitle="Currently subscribed">
          <ul className="iris-feeds">
            <li>CISA ICS-CERT advisories</li>
            <li>MITRE ATT&CK for ICS</li>
            <li>Vendor bulletins (Siemens, Rockwell, Schneider, ABB, GE, Honeywell, Emerson, Yokogawa)</li>
            <li>NCSC UK · BSI Germany · ANSSI France · CERT-EE Estonia</li>
            <li>STIX/TAXII 2.1 (custom feeds)</li>
            <li>ENTSO-E Transparency Platform</li>
            <li>Sentinel Hub (Sentinel-1/2 satellite)</li>
            <li>MarineTraffic AIS · ADS-B Exchange</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
